/** Donut chart for the dashboard's session-status breakdown.
 *
 * SVG arcs computed inline (no chart library). Four segments — closed,
 * live, created, expired — colored to match the StatusPill palette so
 * the dashboard stays visually coherent across the donut + the status
 * bar + the row pills on /sessions.
 *
 * If the total is zero, renders a single muted ring so the slot
 * doesn't collapse.
 */


type Segment = { label: string; value: number; color: string };


interface Props {
  closed: number;
  live: number;
  created: number;
  expired: number;
  /** Label strings already i18n'd by the caller — we keep this
   * component locale-free so it's easy to unit-test. */
  labels: { closed: string; live: string; created: string; expired: string };
  size?: number;
  /** Localised accessible label (component stays locale-free). */
  ariaLabel?: string;
}


export function StatusDonut({
  closed,
  live,
  created,
  expired,
  labels,
  size = 160,
  ariaLabel,
}: Props) {
  const segments: Segment[] = [
    { label: labels.closed, value: closed, color: "rgb(16 185 129)" },  // emerald-500
    { label: labels.live, value: live, color: "rgb(14 165 233)" },      // sky-500
    { label: labels.created, value: created, color: "rgb(100 116 139)" }, // slate-500
    { label: labels.expired, value: expired, color: "rgb(245 158 11)" }, // amber-500
  ];
  const total = segments.reduce((s, x) => s + x.value, 0);
  const r = 56;
  const c = 2 * Math.PI * r;

  let offset = 0;
  return (
    <div className="flex flex-col items-center gap-3 sm:flex-row sm:items-start sm:gap-6">
      <svg
        viewBox="0 0 140 140"
        width={size}
        height={size}
        role="img"
        aria-label={ariaLabel ?? "Session status breakdown"}
      >
        {/* Background ring — keeps the slot visible when total=0. */}
        <circle
          cx="70"
          cy="70"
          r={r}
          fill="none"
          stroke="rgb(30 41 59)"
          strokeWidth="18"
        />
        {total > 0 &&
          segments.map((seg) => {
            if (seg.value === 0) return null;
            const len = (seg.value / total) * c;
            const dasharray = `${len} ${c - len}`;
            const el = (
              <circle
                key={seg.label}
                cx="70"
                cy="70"
                r={r}
                fill="none"
                stroke={seg.color}
                strokeWidth="18"
                strokeDasharray={dasharray}
                strokeDashoffset={-offset}
                transform="rotate(-90 70 70)"
                strokeLinecap="butt"
              />
            );
            offset += len;
            return el;
          })}
        <text
          x="70"
          y="68"
          textAnchor="middle"
          className="fill-slate-100"
          fontSize="22"
          fontWeight="600"
        >
          {total}
        </text>
        <text
          x="70"
          y="86"
          textAnchor="middle"
          className="fill-slate-500"
          fontSize="10"
          fontWeight="600"
          letterSpacing="0.12em"
        >
          TOTAL
        </text>
      </svg>
      <ul className="grid flex-1 grid-cols-1 gap-x-6 gap-y-2 text-sm">
        {segments.map((seg) => (
          <li key={seg.label} className="flex items-center gap-2">
            <span
              className="h-2.5 w-2.5 shrink-0 rounded-full"
              style={{ backgroundColor: seg.color }}
            />
            <span className="text-nb-text">{seg.label}</span>
            <span className="ml-auto shrink-0 font-mono text-nb-text">
              {seg.value}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
